import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * SignatureCanvas — capture mouse/touch signatures as PNG data URL.
 *
 * Props:
 *  - value: string (data URL) — existing signature to display
 *  - onChange: (dataUrl: string) => void
 *  - label: string
 *  - testid: string
 */
export default function SignatureCanvas({ value, onChange, label = "Unterschrift", testid = "signature-canvas" }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!value);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Configure HiDPI-safe canvas
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#f4f4f5";
    ctx.fillStyle = "#18181b";
    ctx.fillRect(0, 0, rect.width, rect.height);
    // Pre-fill with existing value
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [value]);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function end() {
    if (!drawing) return;
    setDrawing(false);
    setHasSignature(true);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  }
  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#18181b";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
    onChange?.("");
  }

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase text-zinc-500">{label}</label>
        {hasSignature && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="text-[10px] text-zinc-400 hover:text-red-400 inline-flex items-center gap-1"
          >
            <Eraser className="w-3 h-3"/> Leeren
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        width={460}
        height={140}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full h-36 bg-zinc-900 border border-zinc-700 rounded cursor-crosshair touch-none"
      />
      <div className="mt-1 text-[10px] text-zinc-500">
        {hasSignature ? "✓ Unterschrift erfasst" : "Mit Maus oder Finger unterschreiben"}
      </div>
    </div>
  );
}
